/// <reference types="google.maps" />
import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';

export interface HighlightedSegment {
  text: string;
  isMatch: boolean;
}

@Component({
  selector: 'autocomplete-suggestions',
  templateUrl: './autocomplete-suggestions.component.html',
  styleUrls: ['./autocomplete-suggestions.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutocompleteSuggestionsComponent implements OnChanges {

  @Input() suggestions: google.maps.places.AutocompleteSuggestion[] = [];

  @Input() isVisible: boolean = false;

  @Input() isLoading: boolean = false;

  @Output() suggestionSelected: EventEmitter<google.maps.places.AutocompleteSuggestion> =
    new EventEmitter<google.maps.places.AutocompleteSuggestion>();

  @Output() closed: EventEmitter<void> = new EventEmitter<void>();

  activeIndex: number = -1;

  highlightedSegmentsMap: HighlightedSegment[][] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.suggestions) {
      this.activeIndex = -1;
      this.highlightedSegmentsMap = this.suggestions.map(
        (suggestion: google.maps.places.AutocompleteSuggestion) => this.computeHighlightedSegments(suggestion),
      );
    }
  }

  handleKeyDown(event: KeyboardEvent): boolean {
    if (!this.isVisible || !this.suggestions.length) {
      return false;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveSelection(1);

        return true;

      case 'ArrowUp':
        event.preventDefault();
        this.moveSelection(-1);

        return true;

      case 'Enter':
        if (this.activeIndex >= 0 && this.activeIndex < this.suggestions.length) {
          event.preventDefault();
          this.selectSuggestion(this.suggestions[this.activeIndex]);

          return true;
        }

        return false;

      case 'Escape':
        event.preventDefault();
        this.close();

        return true;

      default:
        return false;
    }
  }

  selectSuggestion(suggestion: google.maps.places.AutocompleteSuggestion): void {
    this.suggestionSelected.emit(suggestion);
    this.close();
  }

  close(): void {
    this.activeIndex = -1;
    this.closed.emit();
  }

  onMouseEnter(index: number): void {
    this.activeIndex = index;
  }

  trackBySuggestion(index: number, suggestion: google.maps.places.AutocompleteSuggestion): string {
    return suggestion.placePrediction?.placeId || index.toString();
  }

  private moveSelection(delta: number): void {
    const length: number = this.suggestions.length;

    if (length === 0) {
      return;
    }

    if (this.activeIndex === -1) {
      this.activeIndex = delta > 0 ? 0 : length - 1;
    } else {
      this.activeIndex = (this.activeIndex + delta + length) % length;
    }
  }

  private computeHighlightedSegments(suggestion: google.maps.places.AutocompleteSuggestion): HighlightedSegment[] {
    const fullText: string = suggestion.placePrediction?.text?.text || '';
    const matches: google.maps.places.StringRange[] = suggestion.placePrediction?.text?.matches || [];

    if (!fullText || !matches.length) {
      return [{ text: fullText, isMatch: false }];
    }

    const segments: HighlightedSegment[] = [];
    let lastIndex: number = 0;

    const sortedMatches: google.maps.places.StringRange[] = [...matches].sort(
      (a: google.maps.places.StringRange, b: google.maps.places.StringRange) => (a.startOffset || 0) - (b.startOffset || 0),
    );

    for (const match of sortedMatches) {
      const startOffset: number = match.startOffset || 0;
      const endOffset: number = match.endOffset || startOffset;

      if (startOffset > lastIndex) {
        segments.push({
          text: fullText.substring(lastIndex, startOffset),
          isMatch: false,
        });
      }

      if (endOffset > startOffset) {
        segments.push({
          text: fullText.substring(startOffset, endOffset),
          isMatch: true,
        });
      }

      lastIndex = endOffset;
    }

    if (lastIndex < fullText.length) {
      segments.push({
        text: fullText.substring(lastIndex),
        isMatch: false,
      });
    }

    return segments;
  }

}
